import {Color} from '@benchling/nucleus-ui';
import {css, CSSProperties, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';
import {useSelector} from 'react-redux';

import {assert, assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {
  ReviewerStatus,
  ReviewLifecycleType,
  ReviewStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  formatListWithBoldedNames,
  teamHasValidReviewersForReviewable,
} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import {ReviewBadgeContext, ReviewErrorType} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_PROXIMA_18,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, WHITE} from 'ui-library/Colors';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {StepObject, StepperV2} from 'ui-library/stepper/Stepper';
import * as logging from 'util/Logging';
import {capitalize} from 'util/NameUtil';

import {
  ReviewLifecycleSummaryPanelQuery,
  ReviewLifecycleSummaryPanelQueryVariables,
} from './__generated/ReviewLifecycleSummaryPanelQuery';
import {
  REVIEW_ACTION_TO_DISPLAY_PAST_TENSE,
  REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT,
} from './review-lifecycles/ReviewLifecycleConstants';
import ReviewLifecycleSummaryItem, {
  ReviewLifecycleSummaryAvatar,
  SENT_FOR_REVIEW,
} from './review-lifecycles/ReviewLifecycleSummaryItem';
import {runSendForRLCReviewModal} from './review-lifecycles/runSendForRLCReviewModal';
import ReviewConstants, {ReviewAction, PermissionsGraphQL} from './ReviewConstants';
import ReviewStatusModal from './ReviewStatusModal';

type ReviewLifecycleSummaryOrInaccessibleGraphQL = ReviewLifecycleSummaryPanelQuery['reviewable'];

type ReviewableGraphQL = ExcludeInaccessible<ReviewLifecycleSummaryOrInaccessibleGraphQL>;

type ReviewableEntryGraphQL = ExtractByTypename<ReviewLifecycleSummaryOrInaccessibleGraphQL, 'EntryNode'>;

type ReviewGraphQL = NonNullable<ReviewableGraphQL['review']>;

type ActiveReviewLifecycleVersionGraphQL = NonNullable<ReviewGraphQL['activeReviewLifecycleVersion']>;

type ReviewLifecycleGraphQL = NonNullable<ActiveReviewLifecycleVersionGraphQL['reviewLifecycle']>;

type ReviewChangeGraphQL = NonNullable<ReviewGraphQL>['reviewChanges'][number];

type UserGraphQL = ReviewChangeGraphQL['user'];

type ReviewerGraphQL = NonNullable<NonNullable<ReviewGraphQL>['reviewers']>[number];

function isEntry(data?: {__typename?: string}): data is ReviewableEntryGraphQL {
  return data?.__typename === 'EntryNode';
}

export const REVIEW_LIFECYCLE_SUMMARY_PANEL_QUERY = gql`
  query ReviewLifecycleSummaryPanelQuery($reviewableId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        authors {
          id
          handle
          name
          avatarUrl
        }
        review {
          id
          status
          activeReviewLifecycleVersion {
            id
            lifecycleType
            reviewStages {
              id
              name
              selectedTeam {
                id
                name
                members {
                  id
                  name
                }
              }
            }
            reviewLifecycle {
              id
              organization {
                id
                teams {
                  id
                  name
                }
              }
            }
          }
          reviewChanges {
            id
            createdAt
            newStatus
            user {
              ...UserAvatarInfoGraphQL
            }
            previousStage {
              id
            }
            wasEsigned
            action
            message
          }
          reviewers {
            id
            reviewStatus
            updatedAt
            isUserValidReviewer
            user {
              ...UserAvatarInfoGraphQL
            }
            reviewStage {
              id
              name
              action
              description
              sequentialPositionIndex
              selectedTeam {
                id
                name
                members {
                  id
                  name
                }
                adminsJson
              }
              isTeamReassignable
              allowAuthorReviewers
              allowSenderReviewer
            }
            isTeamReassignable
          }
          reviewSender {
            id
          }
        }
        reviewSource {
          id
        }
      }
      ... on EntryNode {
        id
        template {
          id
          templateCollection {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('UserAvatarInfo').fragment}
`;

export interface ReviewLifecycleSummaryPanelProps {
  permissions: PermissionsGraphQL;
}

export interface EntityByStageId {
  // TODO(BNCH-48243): Should not be nullable, reviewers don't currently support null users, even though the
  // data model allows it.
  [stageId: string]: MinimalEntity | null;
}

export interface IsTeamReassignableByStageId {
  [stageId: string]: boolean;
}

interface PrimaryReviewActionProps {
  primaryReviewAction: ReviewAction;
  userFacingMessage: string;
  sourceId: string;
  existingReviewerUsersByStage?: EntityByStageId;
  activeReviewLifecycleId?: string;
  templateId: string | null;
  templateCollectionId: string | null;
}

interface ReviewStagesProps {
  reviewers: Array<ReviewerGraphQL>;
  reviewableId: string;
  canReassign: boolean;
  canAccept: boolean;
  canReject: boolean;
  lifecycleType: ReviewLifecycleType;
  status: string;
  authors: Array<MinimalEntity> | undefined;
  reviewChanges: Array<ReviewChangeGraphQL>;
  reviewSenderId: string | undefined;
}

interface SentForReviewSummaryItemProps {
  reviewChanges: Array<ReviewChangeGraphQL>;
  reviewSenderId: string | undefined;
}

export function PrimaryReviewAction({
  primaryReviewAction,
  userFacingMessage,
  existingReviewerUsersByStage,
  templateId,
  templateCollectionId,
  sourceId,
  activeReviewLifecycleId,
}: PrimaryReviewActionProps): JSX.Element {
  const {
    setIsDropdownOpen,
    reviewError,
    onReviewChange,
    performanceMetadata,
    reviewableId,
    renderAdditionalReviewInfo,
    reviewId,
  } = ReviewBadgeContext.useContext();
  assert(reviewableId != null, 'No ID for reviewable provided');
  assert(reviewId != null, 'No ID for review provided');
  const validationError =
    reviewError?.type === ReviewErrorType.TOOLTIP ? reviewError.error(reviewableId) : null;
  const disabled = Boolean(validationError);
  const renderedAction = (
    <div
      className={disabled ? css(styles.disabledReviewAction) : css(styles.reviewAction)}
      onClick={() => {
        if (disabled) {
          return;
        }
        setIsDropdownOpen(false);
        switch (primaryReviewAction) {
          case ReviewAction.SEND_FOR_REVIEW: {
            assertAndContinue(
              activeReviewLifecycleId != null,
              'Project RLC should exist if sending for RLC Review.'
            );
            const fileIdKey = getEntryOrFileIdKey(reviewableId);
            runSendForRLCReviewModal({
              reviewableId,
              onRequestComplete: onReviewChange,
              existingReviewerUsersByStage,
              analyticsParams: {
                project_id: sourceId,
                [fileIdKey]: reviewableId,
                reviewable_id: reviewableId,
                template_collection_id: templateCollectionId,
                template_id: templateId,
              },
              renderAdditionalReviewInfo,
              renderReviewError:
                reviewError?.type === ReviewErrorType.CALLBACK ? reviewError.error : undefined,
              performanceMetadata,
            });
            break;
          }
          case ReviewAction.RETRACT:
            ReviewStatusModal.open({
              action: primaryReviewAction,
              onUpdate: onReviewChange,
              fileId: reviewableId,
              reviewId,
              // When canceling a review or reopening a reviewable item, reviewCriteriaGroups are not necessary.
              reviewCriteriaGroups: [],
              isForReviewLifecycle: true,
              message: ReviewUtil.getDefaultReviewComment(ReviewAction.RETRACT, true),
              renderAdditionalReviewInfo,
              renderReviewError:
                reviewError?.type === ReviewErrorType.CALLBACK ? reviewError.error : undefined,
              performanceMetadata,
            });
            break;
          default:
            assertAndContinue(
              false,
              `Invalid Review Action from ReviewLifecycleSummaryPanel: ${primaryReviewAction}`
            );
            break;
        }
      }}
    >
      <div
        {...getTestProps({component: 'PrimaryReviewAction', element: 'userActionMessage'})}
        className={css(styles.reviewActionItem)}
      >
        <BenchlingIcon className={css(styles.reviewIcon)} icon={ReviewStatus.RETRACTED} />
        <div>{userFacingMessage}</div>
      </div>
    </div>
  );
  return disabled ? (
    <Tooltip text={validationError} placement={FloatingPlacement.LEFT}>
      {renderedAction}
    </Tooltip>
  ) : (
    renderedAction
  );
}

export function SentForReviewSummaryItem({
  reviewChanges,
  reviewSenderId,
}: SentForReviewSummaryItemProps): JSX.Element {
  const {reviewableId} = ReviewBadgeContext.useContext();
  assert(reviewableId != null, 'ReviewLifecycleSummaryPanel requires a reviewableId');
  const {
    author,
    timestamp: completedTime,
    wasEsigned,
    message,
  } = findLastSentForReviewFromUserWithTimestamp(reviewChanges);
  return (
    <ReviewLifecycleSummaryItem
      canReassign={false}
      canAccept={false}
      canReject={false}
      canReview={false}
      isUserValidReviewer={true}
      user={{
        id: author.id,
        avatar_url: author.avatarUrl,
        email: author.email,
        handle: author.handle,
        name: author.name,
      }}
      reviewStageAction={SENT_FOR_REVIEW}
      stageStatus={ReviewerStatus.FINISHED}
      completedTime={completedTime}
      reviewableId={reviewableId}
      authors={undefined}
      wasEsigned={wasEsigned}
      message={message}
      reviewSenderId={reviewSenderId}
    />
  );
}

/**
 * Find a review change corresponding to a given review stage since the last review.
 *
 * @param reviewChanges Time-ordered array of all review changes on a reviewable object.
 * @param reviewStageId ID of the review stage which was completed
 *
 */
function findLastReviewChangeForStage(
  reviewStageId: string,
  reviewChanges: Array<ReviewChangeGraphQL>
): ReviewChangeGraphQL | null {
  for (const reviewChange of [...reviewChanges].reverse()) {
    if (reviewChange.action === ReviewAction.SEND_FOR_REVIEW) {
      return null;
    }
    if (reviewChange.previousStage?.id === reviewStageId) {
      return reviewChange;
    }
  }
  return null;
}

function getReviewStages({
  reviewers,
  canReassign,
  canAccept,
  canReject,
  lifecycleType,
  status,
  reviewableId,
  authors,
  reviewChanges,
  reviewSenderId,
}: ReviewStagesProps): Array<StepObject | null> {
  // canReassignRLCSummaryItem is True when the reviewable's review status is NEEDS_REVIEW, the user has READ permissions for
  // this reviewable item, and the user is a reviewable author.
  const authorIds = authors?.map(({id}) => id) ?? [];
  const currentUserId = EntityStore.currentUserId;
  const isCurrentUserAuthor = authorIds.includes(currentUserId ?? '');

  return reviewers.map(
    ({user, updatedAt, reviewStatus, reviewStage, isUserValidReviewer, isTeamReassignable}, index) => {
      const {
        name: stageName,
        action,
        id: stageId,
        description: stageDescription,
        selectedTeam: team,
        isTeamReassignable: isTeamReassignableFromStage,
      } = reviewStage;

      if (user == null) {
        // TODO(BNCH-42332): Handle required reassignment since reviewer does not exist for the stage
        return null;
      }

      let canReassignRLCSummaryItem = canReassign && isCurrentUserAuthor;
      let hasValidReviewers = true;

      // checks team-based review stages when the User is not an author and has READ permissions for this
      // NEEDS_REVIEW reviewable for team-specific re-assignability
      if (!isCurrentUserAuthor && canReassign && team != null) {
        const memberIds = team.members.map((member) => member.id);

        hasValidReviewers = teamHasValidReviewersForReviewable(memberIds, authorIds);
        // if the user is a part of the stage's assigned team, allow the user to reassign stage.
        // this will be checked with the team member reassignment checkbox being checked later.
        const canReassignAsTeamReviewer = currentUserId != null && memberIds.includes(currentUserId);

        // if the team member reassignment checkbox is checked, any team member can reassign that stage
        const isReassignableByTeamMember = Boolean(
          canReassignAsTeamReviewer && isTeamReassignable && isTeamReassignableFromStage
        );

        canReassignRLCSummaryItem = isCurrentUserAuthor || isReassignableByTeamMember;
      }

      const avatar: ReviewLifecycleSummaryAvatar = {
        id: user.id,
        avatar_url: user.avatarUrl,
        email: user.email,
        handle: user.handle,
        name: user.name,
      };
      const reviewStageAction =
        reviewStatus === ReviewerStatus.FINISHED || lifecycleType === ReviewLifecycleType.SELF_REVIEW
          ? REVIEW_ACTION_TO_DISPLAY_PAST_TENSE[action]
          : capitalize(REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT[action]);

      const reviewChange = findLastReviewChangeForStage(reviewStage.id, reviewChanges);
      let wasEsigned = false;
      if (reviewStatus === ReviewerStatus.FINISHED || reviewStatus === ReviewerStatus.REJECTED) {
        wasEsigned = reviewChange?.wasEsigned || false;
      }

      return {
        title: (
          <div
            {...getTestProps({component: 'ReviewStageTitle', element: 'root', key: stageId})}
            className={css(styles.title)}
          >
            {reviewStage.name}
          </div>
        ),
        content: (
          <div
            {...getTestProps({component: 'ReviewStage', element: 'root', key: stageId})}
            className={css(styles.stepContent)}
          >
            <ReviewLifecycleSummaryItem
              canReassign={canReassignRLCSummaryItem}
              canReview={avatar.id === currentUserId && status !== ReviewConstants.REJECTED}
              canAccept={canAccept}
              canReject={canReject}
              user={avatar}
              reviewStageAction={reviewStageAction}
              isUserValidReviewer={isUserValidReviewer}
              hasValidReviewerReplacements={hasValidReviewers}
              stageInfo={{
                id: stageId,
                name: lifecycleType === ReviewLifecycleType.SELF_REVIEW ? undefined : stageName,
                description: stageDescription ?? '',
              }}
              reviewableId={reviewableId}
              completedTime={updatedAt}
              stageStatus={reviewStatus}
              authors={authors}
              wasEsigned={wasEsigned}
              reviewLifecycleAction={action}
              message={reviewChange?.message}
              reviewSenderId={reviewSenderId}
            />
          </div>
        ),
        key: stageId,
        renderNumber: (
          <ReviewerStatusIcon
            reviewerStatus={reviewStatus}
            isUserValidReviewer={isUserValidReviewer}
            stageId={stageId}
          />
        ),
      };
    }
  );
}

function getAuthorAction(
  status: ReviewStatus,
  activeReviewLifecycle: ReviewLifecycleGraphQL | null
): {
  primaryReviewAction: ReviewAction.RETRACT | ReviewAction.SEND_FOR_REVIEW;
  userFacingMessage: string;
} | null {
  switch (status) {
    case ReviewStatus.ACCEPTED:
      return {primaryReviewAction: ReviewAction.RETRACT, userFacingMessage: 'Reopen for review'};
    case ReviewStatus.RETRACTED:
    case ReviewStatus.REJECTED:
      // Does this check actually do anything useful?
      if (activeReviewLifecycle != null) {
        return {primaryReviewAction: ReviewAction.SEND_FOR_REVIEW, userFacingMessage: 'Resend for review'};
      }
      return null;
    default:
      return {primaryReviewAction: ReviewAction.RETRACT, userFacingMessage: 'Cancel review'};
  }
}

/**
 * Finds the most recent review change where the reviewable was sent for review by the user.
 *
 * When blocking snapshots are enabled, there are two SEND_FOR_REVIEW review changes.
 * The first is when the user actually sends reviewable for review, and the second is when the snapshot completes
 * This function ensures we grab that first review change in that case.
 * @param reviewChanges Time-ordered array of all review changes on a reviewable.
 */
/** @package */
export function findLastSentForReviewFromUserWithTimestamp(reviewChanges: Array<ReviewChangeGraphQL>): {
  author: UserGraphQL;
  timestamp: string;
  wasEsigned: boolean;
  message: string | null;
} {
  assert(reviewChanges.length > 0, 'Reviewable item has not had any review change');
  const reversedChanges = [...reviewChanges].reverse();

  let lastSendForReviewIndex = reversedChanges.findIndex(
    ({action}) => action === ReviewAction.SEND_FOR_REVIEW
  );
  assert(lastSendForReviewIndex !== -1, 'Reviewable item should have been sent for review');

  // Searches backwards to check for a blocking snapshot since that would be the user's actual sent for review timestamp.
  // It may not be the immediately previous review change as users can create comment review changes while a snapshot is in progress.
  for (let i = lastSendForReviewIndex; i < reversedChanges.length; i++) {
    const status = reversedChanges[i].newStatus;
    if (status === 'REVIEW_SNAPSHOT_IN_PROGRESS') {
      lastSendForReviewIndex = i;
      break;
      // If we find RETRACT/REJECT before REVIEW_SNAPSHOT_IN_PROGRESS, review doesn't have a blocking snapshot and we exit early.
    } else if (status === ReviewStatus.RETRACTED || status === ReviewStatus.REJECTED) {
      break;
    }
  }
  const lastSentForReviewByUser = reversedChanges[lastSendForReviewIndex];
  return {
    author: lastSentForReviewByUser.user,
    timestamp: lastSentForReviewByUser.createdAt,
    wasEsigned: lastSentForReviewByUser.wasEsigned || false,
    message: lastSentForReviewByUser.message,
  };
}

/** @package */
export function MissingReviewersWarning({stages}: {stages: Array<string>}): JSX.Element {
  const missingReviewerStagesString = formatListWithBoldedNames(stages);
  const message = `No reviewer available for ${pluralize(
    stages.length,
    'stage'
  )}: ${missingReviewerStagesString}. Please consult your admin.`;

  return (
    <InlineBanner
      bannerType={InlineBannerType.ERROR}
      // oxlint-disable-next-line react/no-danger
      supportingInfo={<div dangerouslySetInnerHTML={{__html: message}} />}
    />
  );
}

export function ReassignStatusIcon(): JSX.Element {
  return (
    <div className={css(styles.reassignIconContainer)}>
      <BenchlingIcon icon="arrow-right" className={css(styles.reassignIcon)} />
    </div>
  );
}

function ReviewerStatusIcon({
  reviewerStatus,
  isUserValidReviewer,
  stageId,
}: {
  reviewerStatus: ReviewerStatus;
  isUserValidReviewer: boolean;
  stageId: string;
}): JSX.Element {
  let icon: JSX.Element;
  switch (reviewerStatus) {
    case ReviewerStatus.FINISHED:
      icon = <BenchlingIcon icon="done" className={css(styles.completedIcon)} />;
      break;
    case ReviewerStatus.BLOCKED:
      if (!isUserValidReviewer) {
        icon = <ReassignStatusIcon />;
      } else {
        icon = <BenchlingIcon icon="lock" className={css(styles.lockIcon)} />;
      }
      break;
    case ReviewerStatus.PENDING:
      if (!isUserValidReviewer) {
        icon = <ReassignStatusIcon />;
      } else {
        icon = <div className={css(styles.pendingIcon)} />;
      }
      break;
    case ReviewerStatus.REJECTED:
      icon = <BenchlingIcon icon="exclamation-circle" className={css(styles.rejectedIcon)} />;
      break;
    default:
      assert(false, `Received an unexpected reviewer status: ${reviewerStatus}`);
  }
  return (
    <div
      className={css(styles.iconContainer)}
      {...getTestProps({component: 'ReviewerStatusIcon', element: 'root', key: stageId})}
    >
      {icon}
    </div>
  );
}

/**
 * Creates a review summary panel for reviewables detailing the state for each review stage in the review lifecycle.
 * Should only be rendered for reviewables that have been sent for review at least once with a review lifecycle.
 */
export default function ReviewLifecycleSummaryPanel({
  permissions,
}: ReviewLifecycleSummaryPanelProps): JSX.Element | null {
  const {reviewableId, openHistory} = ReviewBadgeContext.useContext();
  assert(reviewableId != null, 'ReviewLifecycleSummaryPanel requires a reviewableId');
  const activeEditorLock = useSelector(
    (state: BenchlingState) => state.ui.documentEditorLockState[reviewableId] ?? null
  );

  const result = useBenchlingQuery<
    ReviewLifecycleSummaryPanelQuery,
    ReviewLifecycleSummaryPanelQueryVariables
  >(REVIEW_LIFECYCLE_SUMMARY_PANEL_QUERY, {variables: {reviewableId}, fetchPolicy: 'network-only'});

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  const {reviewable} = result.data;

  if (isInaccessible(reviewable)) {
    logging.error('User did not have access to the reviewable item while fetching review lifecycle data');
    return null;
  }

  const {review, authors} = reviewable;

  assert(
    review != null,
    'Attempting to render a ReviewLifecycleSummaryPanel for a reviewable item that does not have an attached review'
  );

  const {status, reviewChanges, reviewers, activeReviewLifecycleVersion, reviewSender} = review;
  const reviewSenderId = reviewSender ? reviewSender.id : undefined;

  assert(activeReviewLifecycleVersion != null, 'Trying to view review summary without RLC version');
  assert(reviewers != null, 'Trying to view review summary without configured reviewers');

  const {reviewLifecycle, lifecycleType} = activeReviewLifecycleVersion;

  const reviewActionToPermission = {
    [ReviewAction.RETRACT]: Boolean(permissions.canRetract),
    [ReviewAction.SEND_FOR_REVIEW]: Boolean(permissions.canSendForReview),
  };
  const isSelfReview = lifecycleType === ReviewLifecycleType.SELF_REVIEW;

  const maybeAuthorAction = getAuthorAction(status, reviewLifecycle);

  const missingTeamReviewerStages: Array<string> = [];
  activeReviewLifecycleVersion.reviewStages.forEach(({name, selectedTeam}) => {
    if (selectedTeam != null) {
      const memberIds = selectedTeam.members.map((member) => member.id);
      const authorIds = authors != null ? authors.map((author) => author.id) : [];

      if (!teamHasValidReviewersForReviewable(memberIds, authorIds)) {
        missingTeamReviewerStages.push(name);
      }
    }
  });

  return (
    <div {...getTestProps({component: 'ReviewLifecycleSummaryPanel', element: 'root'})}>
      {missingTeamReviewerStages.length > 0 && <MissingReviewersWarning stages={missingTeamReviewerStages} />}
      <div className={css(styles.topBox)}>
        <div className={css(styles.summary)}>
          <span>Summary</span>
          {openHistory && (
            <Tooltip text="See full history">
              <Button
                size={ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="clipboard-list"
                onClick={openHistory}
              />
            </Tooltip>
          )}
        </div>
        {/* User may not have permission to perform action or project may not be configured to allow an
        RLC related author action anymore. */}
        {maybeAuthorAction &&
          reviewActionToPermission[maybeAuthorAction.primaryReviewAction] &&
          !(
            maybeAuthorAction.primaryReviewAction === ReviewAction.SEND_FOR_REVIEW &&
            activeEditorLock &&
            DocumentEditorLockUtil.isLockedInRealtime(activeEditorLock)
          ) && (
            <PrimaryReviewAction
              primaryReviewAction={maybeAuthorAction.primaryReviewAction}
              userFacingMessage={maybeAuthorAction.userFacingMessage}
              existingReviewerUsersByStage={reviewers.reduce(
                (acc: EntityByStageId, reviewer: ReviewerGraphQL) => {
                  acc[reviewer.reviewStage.id] = reviewer.user;
                  return acc;
                },
                {}
              )}
              sourceId={reviewable.reviewSource.id}
              activeReviewLifecycleId={reviewLifecycle.id}
              templateId={isEntry(reviewable) ? (reviewable.template?.id ?? null) : null}
              templateCollectionId={
                isEntry(reviewable) ? (reviewable.template?.templateCollection.id ?? null) : null
              }
            />
          )}
      </div>
      <div className={css(styles.panel)}>
        <StepperV2
          hideVerticalRule={lifecycleType === ReviewLifecycleType.PARALLEL}
          steps={[
            ...(isSelfReview
              ? []
              : [
                  {
                    title: (
                      <div
                        {...getTestProps({component: 'ReviewStageTitle', element: 'root'})}
                        className={css(styles.title)}
                      >
                        Sent for review
                      </div>
                    ),
                    content: (
                      <div className={css(styles.stepContent)}>
                        <SentForReviewSummaryItem
                          reviewChanges={reviewChanges}
                          reviewSenderId={reviewSenderId}
                        />
                        {lifecycleType === ReviewLifecycleType.PARALLEL && (
                          <div className={css(styles.horizontal)} />
                        )}
                      </div>
                    ),
                    renderNumber: (
                      <ReviewerStatusIcon
                        reviewerStatus={ReviewerStatus.FINISHED}
                        isUserValidReviewer={true}
                        stageId="sent_for_review"
                      />
                    ),
                    key: '',
                  },
                ]),
            ...getReviewStages({
              reviewers,
              canReassign: Boolean(permissions.canReassignRlcReviewer) && status !== ReviewConstants.REJECTED,
              canAccept: Boolean(permissions.canAccept),
              canReject: Boolean(permissions.canReject),
              lifecycleType,
              status,
              authors,
              reviewChanges,
              reviewSenderId,
              reviewableId,
            }),
          ]}
        />
      </div>
    </div>
  );
}

const reviewActionStyle = {
  ...FONT_PROXIMA_12,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  margin: '8px 0px',
  color: Color.BLUE_500,
} as CSSProperties;

const styles = StyleSheet.create({
  title: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  iconContainer: {
    height: 16,
  },
  stepContent: {
    marginBottom: 20,
    width: 214,
  },
  completedIcon: {
    color: Color.GREEN_600,
    width: 16,
    height: 16,
  },
  lockIcon: {
    color: Color.GRAY_500,
    width: 16,
    height: 16,
  },
  rejectedIcon: {
    color: Color.RED_500,
    width: 16,
    height: 16,
  },
  reassignIcon: {
    color: WHITE,
    width: 10,
    height: 10,
    display: 'block',
    margin: 'auto',
  },
  reassignIconContainer: {
    backgroundColor: Color.RED_500,
    width: 16,
    height: 16,
    borderRadius: 8,
    alignContent: 'center',
  },
  pendingIcon: {
    border: `1px solid ${Color.GRAY_500}`,
    borderRadius: 8,
    height: 16,
    width: 16,
  },
  reviewStagesContainer: {
    width: '100%',
  },
  arrow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4px 8px 4px 14px',
  },
  parallel: {
    margin: '0px 0px',
    paddingBottom: '12px',
  },
  horizontal: {
    margin: '12px 0px',
    border: `0.5px solid ${GRAY}`,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    margin: '0px 0px',
  },
  summary: {
    ...FONT_PROXIMA_18,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  topBox: {
    display: 'flex',
    flexDirection: 'column',
    margin: '8px 0px',
  },
  reviewAction: {
    ...reviewActionStyle,
  },
  disabledReviewAction: {
    ...reviewActionStyle,
    color: Color.GRAY_500,
  },
  reviewIcon: {
    marginRight: '4px',
    width: '12px',
    height: '12px',
  },
  reviewActionItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: '16px',
  },
});
